import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Tooltip } from '@material-ui/core'
import { formatClock, parseClock, periodLabel } from '../../../hooks/useStudioScoreboard'
import COLORS from '../../../utils/constants/colors'

const CONDENSED = "'Fira Sans Condensed', 'Roboto Condensed', Roboto, sans-serif"

const useStyles = makeStyles({
  // Relative so the status message can sit under the cards without being laid out beside them:
  // in flow it widened the row enough to wrap the rest of the control bar onto a second line.
  group: { position: 'relative', display: 'flex', alignItems: 'center', gap: '12px' },
  // Locked means another source is feeding the board. The panel stays on screen — it is how the
  // operator sees what is going out and where to switch back — but it is visibly not theirs to
  // drive, and every control inside it is disabled rather than merely dimmed.
  locked: { opacity: 0.4, transition: 'opacity 140ms ease-out' },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    height: '80px',
    boxSizing: 'border-box',
    background: '#1b1d21',
    border: '1px solid #2a2d33',
    borderRadius: '16px',
    padding: '0 12px',
  },
  teamBlock: { display: 'flex', flexDirection: 'column', gap: '2px' },
  teamName: {
    fontFamily: CONDENSED,
    fontSize: '13px',
    fontWeight: 500,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    color: '#8d939c',
    maxWidth: '190px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  scoreRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  field: {
    padding: 0,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    borderRadius: '6px',
    fontFamily: CONDENSED,
    fontWeight: 700,
    color: '#fff',
    fontVariantNumeric: 'tabular-nums',
    '&:focus': {
      background: 'rgba(125, 102, 252, 0.16)',
      boxShadow: '0 0 0 3px rgba(125, 102, 252, 0.5)',
    },
    '&:disabled': { opacity: 0.5 },
  },
  score: { width: '72px', fontSize: '38px', lineHeight: 1.05 },
  clock: {
    width: '104px',
    padding: '2px 4px',
    textAlign: 'center',
    fontSize: '42px',
    lineHeight: 1.05,
    borderRadius: '8px',
  },
  points: { display: 'flex', gap: '6px' },
  pointBtn: {
    width: '38px',
    height: '42px',
    borderRadius: '8px',
    border: '1px solid #3a3e46',
    background: '#24272d',
    color: '#fff',
    fontFamily: CONDENSED,
    fontSize: '19px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'background 140ms ease-out',
    '&:hover:enabled': { background: '#31353d' },
    '&:active': { transform: 'translateY(1px)' },
    '&:disabled': { opacity: 0.4, cursor: 'default' },
  },
  iconBtn: {
    borderRadius: '8px',
    border: '1px solid #3a3e46',
    background: '#24272d',
    color: '#cfd3d9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover:enabled': { background: '#31353d', color: '#fff' },
    '&:disabled': { opacity: 0.4, cursor: 'default' },
  },
  stepBtn: { width: '34px', height: '34px' },
  resetBtn: { width: '42px', height: '46px' },
  periodBlock: { display: 'flex', alignItems: 'center', gap: '4px' },
  periodLabel: {
    minWidth: '64px',
    textAlign: 'center',
    fontFamily: CONDENSED,
    fontWeight: 700,
    fontSize: '26px',
    letterSpacing: '0.04em',
    color: '#fff',
  },
  divider: { width: '1px', height: '48px', background: '#2a2d33' },
  runBtn: {
    width: '52px',
    height: '46px',
    borderRadius: '10px',
    border: 'none',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    background: COLORS.purple,
    transition: 'background 140ms ease-out',
    '&:active': { transform: 'translateY(1px)' },
    '&:disabled': { opacity: 0.4, cursor: 'default' },
  },
  runBtnRunning: { background: '#2E2C33' },
  status: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    right: 0,
    textAlign: 'center',
    fontFamily: CONDENSED,
    fontSize: '12px',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#F04E63',
    whiteSpace: 'nowrap',
  },
})

const Chevron = ({ back }) => (
  <svg
    width='17'
    height='17'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.4'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d={back ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
  </svg>
)
Chevron.propTypes = { back: PropTypes.bool }
Chevron.defaultProps = { back: false }

const RunIcon = ({ running }) =>
  running ? (
    <svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
      <rect x='6' y='4' width='4' height='16' rx='1' />
      <rect x='14' y='4' width='4' height='16' rx='1' />
    </svg>
  ) : (
    <svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
      <path d='M7 4l13 8-13 8z' />
    </svg>
  )
RunIcon.propTypes = { running: PropTypes.bool.isRequired }

const ResetIcon = () => (
  <svg
    width='18'
    height='18'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M3 12a9 9 0 1 0 2.6-6.4L3 8' />
    <path d='M3 3v5h5' />
  </svg>
)

const selectAll = (event) => event.target.select()

const HOME_LABEL = 'Home Team'
const AWAY_LABEL = 'Away Team'

const ScoreboardControls = ({
  board,
  connected,
  error,
  locked,
  addPoints,
  setScore,
  stepPeriod,
  setClockMs,
  toggleClock,
  resetClock,
}) => {
  const classes = useStyles()
  // Drafts keep a field editable while the clock keeps ticking underneath it.
  const [drafts, setDrafts] = useState({})
  const disabled = locked || !connected

  const draftOf = (key, value) => (drafts[key] !== undefined ? drafts[key] : value)
  const setDraft = (key, value) => setDrafts((current) => ({ ...current, [key]: value }))
  const clearDraft = (key) =>
    setDrafts((current) => {
      const next = { ...current }
      delete next[key]
      return next
    })

  const commitScore = (team) => {
    const draft = drafts[team]
    if (draft !== undefined) setScore(team, draft)
    clearDraft(team)
  }

  const commitClock = () => {
    const draft = drafts.clock
    if (draft !== undefined) {
      const ms = parseClock(draft)
      if (ms !== null) setClockMs(ms)
    }
    clearDraft('clock')
  }

  const onFieldKeyDown = (event) => {
    if (event.key === 'Enter') event.target.blur()
  }

  const pointButtons = (team) =>
    [1, 2, 3].map((points) => (
      <button
        key={points}
        type='button'
        className={classes.pointBtn}
        onClick={() => addPoints(team, points)}
        disabled={disabled}
        aria-label={`${team === 'home' ? HOME_LABEL : AWAY_LABEL} plus ${points}`}
      >
        {`+${points}`}
      </button>
    ))

  return (
    <div className={`${classes.group} ${locked ? classes.locked : ''}`}>
      <div className={classes.card}>
        <div className={classes.teamBlock}>
          <div className={classes.teamName}>{HOME_LABEL}</div>
          <div className={classes.scoreRow}>
            <input
              className={`${classes.field} ${classes.score}`}
              style={{ textAlign: 'left' }}
              value={draftOf('home', String(board.home))}
              onChange={(event) => setDraft('home', event.target.value.replace(/\D/g, ''))}
              onFocus={selectAll}
              onBlur={() => commitScore('home')}
              onKeyDown={onFieldKeyDown}
              disabled={disabled}
              inputMode='numeric'
              aria-label={`${HOME_LABEL} score`}
            />
            <div className={classes.points}>{pointButtons('home')}</div>
          </div>
        </div>
      </div>

      <div className={classes.card} style={{ gap: '12px' }}>
        <div className={classes.periodBlock}>
          <Tooltip title='Previous period' arrow>
            <span>
              <button
                type='button'
                className={`${classes.iconBtn} ${classes.stepBtn}`}
                onClick={() => stepPeriod(-1)}
                disabled={disabled || board.period <= 1}
                aria-label='Previous period'
              >
                <Chevron back />
              </button>
            </span>
          </Tooltip>
          <div className={classes.periodLabel}>{periodLabel(board.period)}</div>
          <Tooltip title='Next period' arrow>
            <span>
              <button
                type='button'
                className={`${classes.iconBtn} ${classes.stepBtn}`}
                onClick={() => stepPeriod(1)}
                disabled={disabled}
                aria-label='Next period'
              >
                <Chevron />
              </button>
            </span>
          </Tooltip>
        </div>
        <div className={classes.divider} />
        <input
          className={`${classes.field} ${classes.clock}`}
          value={draftOf('clock', formatClock(board.remainingMs))}
          onChange={(event) => setDraft('clock', event.target.value)}
          onFocus={selectAll}
          onBlur={commitClock}
          onKeyDown={onFieldKeyDown}
          disabled={disabled}
          aria-label='Game clock'
        />
        <Tooltip title={board.running ? 'Pause the clock' : 'Start the clock'} arrow>
          <span>
            <button
              type='button'
              className={`${classes.runBtn} ${board.running ? classes.runBtnRunning : ''}`}
              onClick={toggleClock}
              disabled={disabled || (!board.running && board.remainingMs <= 0)}
              aria-label={board.running ? 'Pause the clock' : 'Start the clock'}
            >
              <RunIcon running={board.running} />
            </button>
          </span>
        </Tooltip>
        <Tooltip title='Reset to period length' arrow>
          <span>
            <button
              type='button'
              className={`${classes.iconBtn} ${classes.resetBtn}`}
              onClick={resetClock}
              disabled={disabled}
              aria-label='Reset the clock'
            >
              <ResetIcon />
            </button>
          </span>
        </Tooltip>
      </div>

      <div className={classes.card}>
        <div className={classes.teamBlock} style={{ alignItems: 'flex-end' }}>
          <div className={classes.teamName}>{AWAY_LABEL}</div>
          <div className={classes.scoreRow}>
            <div className={classes.points}>{pointButtons('away')}</div>
            <input
              className={`${classes.field} ${classes.score}`}
              style={{ textAlign: 'right' }}
              value={draftOf('away', String(board.away))}
              onChange={(event) => setDraft('away', event.target.value.replace(/\D/g, ''))}
              onFocus={selectAll}
              onBlur={() => commitScore('away')}
              onKeyDown={onFieldKeyDown}
              disabled={disabled}
              inputMode='numeric'
              aria-label={`${AWAY_LABEL} score`}
            />
          </div>
        </div>
      </div>

      {error && (
        <span className={classes.status} role='status'>
          {error}
        </span>
      )}
    </div>
  )
}

ScoreboardControls.propTypes = {
  board: PropTypes.shape({
    period: PropTypes.number.isRequired,
    home: PropTypes.number.isRequired,
    away: PropTypes.number.isRequired,
    remainingMs: PropTypes.number.isRequired,
    running: PropTypes.bool.isRequired,
  }).isRequired,
  connected: PropTypes.bool,
  error: PropTypes.string,
  locked: PropTypes.bool,
  addPoints: PropTypes.func.isRequired,
  setScore: PropTypes.func.isRequired,
  stepPeriod: PropTypes.func.isRequired,
  setClockMs: PropTypes.func.isRequired,
  toggleClock: PropTypes.func.isRequired,
  resetClock: PropTypes.func.isRequired,
}

ScoreboardControls.defaultProps = {
  connected: false,
  error: null,
  locked: false,
}

export default ScoreboardControls
